import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import { useMapFullscreen } from "../../context/MapFullscreenContext";

// Template122: Día 4 Mapa Camden Market (Page24 guia-londres). Todo desde backend.
// page.texts[0]=header, texts[1]=explicación | page.ui, page.maps[0], page.images, page.canvasImages, page.sections[0]=Regents Park
const Template122 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [cyberdogImageError, setCyberdogImageError] = useState(false);
	const mapFullscreenContext = useMapFullscreen();
	const setMapFullscreen = mapFullscreenContext?.setMapFullscreen;

	const openFullscreen = () => {
		setFullscreen(true);
		if (typeof setMapFullscreen === "function") setMapFullscreen(true);
	};
	const closeFullscreen = () => {
		setFullscreen(false);
		if (typeof setMapFullscreen === "function") setMapFullscreen(false);
	};

	useEffect(() => {
		return () => {
			if (typeof setMapFullscreen === "function") setMapFullscreen(false);
		};
	}, [setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const explanationText = page.texts?.[1]?.content;
	const ui = page?.ui ?? page?.data?.ui ?? page?.data?.data?.ui ?? {};

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const mapaOffline = getImageSrc("mapa-offline");
	const umbrellaImage = getCanvasImageUrl("umbrella");
	const cyberdogImage = getCanvasImageUrl("cyberdog");
	const mapUrl = page.maps?.[0];
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const bottomSection = sections[0];
	const tooltipUmbrella = ui.tooltipUmbrella;

	const actualHeaderColor = headerBackgroundColor || "#de5624";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={closeFullscreen}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						{ui.backButton || "← Volver a la guía"}
					</Box>
				</Box>
				<iframe
					title={ui.mapTitleFullscreen || "Mapa grande"}
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						zIndex: 2,
					}}
				>
					{headerText}
					{logoImage && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* MAPA */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					minHeight: 320,
					marginTop: "-70px",
					position: "relative",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				>
					{!loaded && isOnline && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: `4px solid ${actualHeaderColor}`,
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									"@keyframes spin": {
										"0%": { transform: "rotate(0deg)" },
										"100%": { transform: "rotate(360deg)" },
									},
								}}
							/>
						</Box>
					)}

					{isOnline && mapUrl && (
						<Box
							component="button"
							onClick={openFullscreen}
							sx={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: actualHeaderColor,
								color: "#fff!important",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

								border: "none",
								cursor: "pointer",
							}}
						>
							{ui.fullscreenButton || "Ver mapa grande"}
						</Box>
					)}

					{isOnline && mapUrl ? (
						<iframe
							title={ui.mapTitleEmbed || "Mapa Camden"}
							src={mapUrl}
							width="100%"
							height="100%"
							style={{ border: 0 }}
							onLoad={() => setLoaded(true)}
						></iframe>
					) : mapaOffline ? (
						<img
							src={mapaOffline}
							alt=""
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "relative",
							}}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					) : null}
				</Box>

				{/* Tooltip Umbrella Alley */}
				{(tooltipUmbrella?.title || tooltipUmbrella?.body) && (
					<Box
						sx={{
							position: "absolute",
							top: 80,
							left: 10,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px",
							padding: "10px 12px",
							fontSize: 11,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 160,
							zIndex: 3,
						}}
					>
						{!isSmallScreen && umbrellaImage && (
							<img
								src={umbrellaImage}
								alt=""
								style={{ width: 60, zIndex: 200 }}
							/>
						)}
						<Box>
							{tooltipUmbrella.title && (
								<strong>{tooltipUmbrella.title}</strong>
							)}
							{tooltipUmbrella.title && tooltipUmbrella.body && <br />}
							{tooltipUmbrella.body}
						</Box>
					</Box>
				)}

				{cyberdogImage && !cyberdogImageError && (
					<img
						src={cyberdogImage}
						alt=""
						onError={() => setCyberdogImageError(true)}
						style={{
							width: 110,
							position: "absolute",
							bottom: -10,
							left: -18,
						}}
					/>
				)}
			</Box>

			{/* EXPLICACIÓN */}
			{explanationText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontSize: 12,
						backgroundColor: "#fff",
						color: "#333",
					}}
				>
					{explanationText}
				</Box>
			)}

			{/* Regents Park y Baker Street (section 0) */}
			{(bottomSection?.title || bottomSection?.body) && (
				<Box
					data-block-id="text-2"
					sx={{
						display: "flex",
						gap: "16px",
						alignItems: "center",
						padding: "12px 16px",
						paddingTop: 0,
						backgroundColor: "#ffffff",
						position: "relative",
					}}
				>
					<Box
						sx={{
							backgroundColor: "#f4e4df",
							padding: "12px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ paddingLeft: "8px" }}>
							{bottomSection?.title && (
								<Box component="h3" sx={h3}>
									{bottomSection.title}
								</Box>
							)}
							{bottomSection?.body && (
								<Box component="p" sx={p}>
									{bottomSection.body}
								</Box>
							)}
						</Box>
					</Box>
					{bottomSection?.sabiasQue?.[0] && (
						<Box
							sx={{
								position: "absolute",
								top: isSmallScreen ? -14 : -20,
								left: isSmallScreen ? "50%" : "35%",
							}}
						>
							<SabiasQue
								icono={bottomSection.sabiasQue[0].icono}
								texto={bottomSection.sabiasQue[0].texto}
							/>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};

Template122.thumbnail = "/images/template-1.png";

export default Template122;
